/**
 * The painted guidance lane, built from the ROAD PIECES (ADR-065 amendment J).
 *
 * The white road-edge line is drawn straight from a piece's own geometry:
 * `halfWidth - ROAD_EDGE_LINE_INSET_M`, in world space, no tolerances. The
 * blue guidance lane is now built the same way — the route only decides WHICH
 * pieces, in WHAT order, and where each leg starts and ends. Consecutive legs
 * are stitched by a single tangent arc. Nothing is probed, filtered, median-ed
 * or rate-limited, so the lane sits at exactly one distance from the kerb for
 * the whole length of a road, on every map.
 *
 * The planner's per-station band (`CorridorStation[]`, d-offsets from the
 * route) is PROJECTED from the finished ribbon rather than being its source.
 *
 * Pure module: no DOM, no Babylon.
 */

import { wrapAngle, type Vec2 } from "../core/vec";
import { CORRIDOR_STATION_M, ROAD_EDGE_LINE_INSET_M } from "./constants";
import type { CorridorStation } from "./corridor";
import { extrudeEdges, type GuideEdges } from "./guide_geometry";
import { pieceForward, pieceSdf, type BandPiece, type RingPiece, type RoadNetwork, type RoadPiece } from "./network";
import type { DeliveryPath, PathWaypoint } from "./path";
import type { RobotShape } from "./world";

export type { GuideEdges };

/** The lane's right edge sits on the road-edge line: this far inside the kerb. */
const INSET = ROAD_EDGE_LINE_INSET_M;
/** Painted lane width (m) — constant except on a road too narrow to hold it. */
const LANE_W = 1.15;
const LANE_HALF = LANE_W / 2;
/** Fillet radius at a concave (reflex) kerb corner — a right turn. */
const R_CONCAVE = 2.5;
/** Crossing-window ramp length each side (m). */
const TAPER = 8;
/** Centre-line sampling step (m). */
const SAMPLE = 0.5;
/** Legs shorter than this are absorbed by their neighbour. */
const MIN_LEG_M = 3;
/** Below this turn the join is a straight stitch, not an arc. */
const STRAIGHT_RAD = (15 * Math.PI) / 180;
/** A left turn's sweeping fillet never exceeds this radius. */
const R_LEFT_MAX = 12;
/** No fillet is ever tighter than this. */
const R_MIN = 1.2;
/** Clear of the far kerb on a road too narrow for the full lane. */
const NARROW_CLEAR = 0.3;
/** Largest heading change between two samples of an arc (rad). */
const ARC_STEP_RAD = 0.2;

// --- small vector helpers ------------------------------------------------

function unit(dx: number, dz: number): Vec2 {
  const l = Math.hypot(dx, dz);
  return l < 1e-9 ? { x: 0, z: 1 } : { x: dx / l, z: dz / l };
}

/** Right normal of a direction: (dz, -dx), matching the sim's frame. */
function rightOf(d: Vec2): Vec2 {
  return { x: d.z, z: -d.x };
}

function dedupe(pts: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of pts) {
    const q = out[out.length - 1];
    if (q && Math.hypot(p.x - q.x, p.z - q.z) < 1e-6) continue;
    out.push({ x: p.x, z: p.z });
  }
  return out;
}

function cumLen(pts: readonly Vec2[]): number[] {
  const c = [0];
  for (let i = 1; i < pts.length; i++) {
    c.push(c[i - 1]! + Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.z - pts[i - 1]!.z));
  }
  return c;
}

function polyAt(pts: readonly Vec2[], cum: readonly number[], t: number): Vec2 {
  const total = cum[cum.length - 1]!;
  if (t <= 0) return { ...pts[0]! };
  if (t >= total) return { ...pts[pts.length - 1]! };
  let i = 0;
  while (i + 2 < pts.length && cum[i + 1]! < t) i++;
  const segLen = cum[i + 1]! - cum[i]!;
  const u = segLen < 1e-9 ? 0 : (t - cum[i]!) / segLen;
  return {
    x: pts[i]!.x + (pts[i + 1]!.x - pts[i]!.x) * u,
    z: pts[i]!.z + (pts[i + 1]!.z - pts[i]!.z) * u,
  };
}

/** Arc-length parameter of the point on `pts` nearest to (x, z). */
function polyProject(pts: readonly Vec2[], cum: readonly number[], x: number, z: number): number {
  let bestT = 0;
  let bestD = Infinity;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const vx = b.x - a.x;
    const vz = b.z - a.z;
    const vv = vx * vx + vz * vz;
    if (vv < 1e-12) continue;
    let u = ((x - a.x) * vx + (z - a.z) * vz) / vv;
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    const d = Math.hypot(x - (a.x + vx * u), z - (a.z + vz * u));
    if (d < bestD) {
      bestD = d;
      bestT = cum[i]! + Math.sqrt(vv) * u;
    }
  }
  return bestT;
}

/** Foot of (x, z) on the infinite line through `p` with direction `d`. */
function footOnLine(p: Vec2, d: Vec2, x: number, z: number): Vec2 {
  const u = (x - p.x) * d.x + (z - p.z) * d.z;
  return { x: p.x + d.x * u, z: p.z + d.z * u };
}

/**
 * Offset a polyline sideways by `off` (positive = right of its own
 * direction) — the exact parallel curve.
 *
 * On the OUTER side of a vertex the parallel curve is an arc of radius |off|
 * about that vertex; a mitre there would push the lane away from the road
 * centreline by off*(1/cos(a/2) - 1) and put a hard kink in the paint (5 cm
 * and 18 deg at m09's 18-degree bend). On the INNER side the offset lines
 * meet, so the mitre is exact — capped at 2x the offset where they nearly
 * double back.
 */
function offsetPolyline(pts: readonly Vec2[], off: number): Vec2[] {
  const n = pts.length;
  const dirs: Vec2[] = [];
  for (let i = 0; i + 1 < n; i++) dirs.push(unit(pts[i + 1]!.x - pts[i]!.x, pts[i + 1]!.z - pts[i]!.z));
  const at = (p: Vec2, nr: Vec2): Vec2 => ({ x: p.x + nr.x * off, z: p.z + nr.z * off });
  const out: Vec2[] = [at(pts[0]!, rightOf(dirs[0]!))];
  for (let i = 1; i + 1 < n; i++) {
    const d1 = dirs[i - 1]!;
    const d2 = dirs[i]!;
    const n1 = rightOf(d1);
    const n2 = rightOf(d2);
    const turn = wrapAngle(Math.atan2(d2.x, d2.z) - Math.atan2(d1.x, d1.z));
    if (Math.abs(turn) < 1e-4) {
      out.push(at(pts[i]!, n1));
      continue;
    }
    const cross = d1.x * d2.z - d1.z * d2.x;
    if (cross * off > 0) {
      const v = pts[i]!;
      const r = Math.abs(off);
      const A = at(v, n1);
      const B = at(v, n2);
      const a0 = Math.atan2(A.x - v.x, A.z - v.z);
      const d = wrapAngle(Math.atan2(B.x - v.x, B.z - v.z) - a0);
      const steps = Math.max(1, Math.ceil(Math.abs(d) / ARC_STEP_RAD), Math.ceil((Math.abs(d) * r) / 0.25));
      for (let k = 0; k <= steps; k++) {
        const a = a0 + (d * k) / steps;
        out.push({ x: v.x + Math.sin(a) * r, z: v.z + Math.cos(a) * r });
      }
      continue;
    }
    let mx = n1.x + n2.x;
    let mz = n1.z + n2.z;
    const ml = Math.hypot(mx, mz);
    if (ml < 1e-6) {
      out.push(at(pts[i]!, n1));
      continue;
    }
    mx /= ml;
    mz /= ml;
    const cosHalf = mx * n1.x + mz * n1.z;
    let scale = off / Math.max(0.25, cosHalf);
    const cap = 2 * Math.abs(off);
    if (Math.abs(scale) > cap) scale = Math.sign(scale) * cap;
    out.push({ x: pts[i]!.x + mx * scale, z: pts[i]!.z + mz * scale });
  }
  out.push(at(pts[n - 1]!, rightOf(dirs[dirs.length - 1]!)));
  return out;
}

// --- legs ----------------------------------------------------------------

interface RingLane {
  cx: number;
  cz: number;
  /** Lane-centre radius. */
  R: number;
  /** Unwrapped start/end angle (psi = atan2(x - cx, z - cz)). */
  a0: number;
  a1: number;
}

interface Leg {
  index: number;
  piece: RoadPiece;
  s0: number;
  s1: number;
  /** Lane centre in world space, in travel order. */
  pts: Vec2[];
  /** Lane half-width. */
  half: number;
  /** Band half-width (0 for a ring). */
  hw: number;
  ring: RingLane | null;
  /** The crossing window that replaces the join after this leg, if any. */
  crossAfter: { s0: number; s1: number } | null;
}

function ringPts(r: RingLane): Vec2[] {
  const d = r.a1 - r.a0;
  const n = Math.max(1, Math.ceil((Math.abs(d) * r.R) / SAMPLE));
  const out: Vec2[] = [];
  for (let k = 0; k <= n; k++) {
    const a = r.a0 + (d * k) / n;
    out.push({ x: r.cx + Math.sin(a) * r.R, z: r.cz + Math.cos(a) * r.R });
  }
  return out;
}

/** Angle of (x, z) about a ring centre, unwrapped into the leg's own sense. */
function ringAngleNear(r: RingLane, ref: number, x: number, z: number): number {
  const raw = Math.atan2(x - r.cx, z - r.cz);
  return ref + wrapAngle(raw - ref);
}

function buildBandLeg(path: DeliveryPath, index: number, piece: BandPiece, s0: number, s1: number): Leg | null {
  let pts = dedupe(piece.pts.map((p) => ({ x: p.x, z: p.z })));
  if (pts.length < 2) return null;
  const start = path.point(s0);
  const fwd = pieceForward(piece, start.x, start.z);
  const along = Math.sin(start.theta) * Math.sin(fwd) + Math.cos(start.theta) * Math.cos(fwd);
  if (along < 0) pts = pts.slice().reverse();

  const hw = piece.halfWidth;
  // Constant lane width, except where the road cannot hold it: then the lane
  // narrows so its LEFT edge keeps NARROW_CLEAR from the far kerb.
  let w = LANE_W;
  if (2 * hw < LANE_W + INSET + NARROW_CLEAR) w = Math.max(0.36, 2 * hw - INSET - NARROW_CLEAR);
  const off = hw - INSET - w / 2;

  const lane = offsetPolyline(pts, off);
  const cum = cumLen(lane);
  const total = cum[cum.length - 1]!;
  if (total < 1e-6) return null;
  const pA = path.point(s0);
  const pB = path.point(s1);
  let t0 = polyProject(lane, cum, pA.x, pA.z);
  let t1 = polyProject(lane, cum, pB.x, pB.z);
  if (t1 < t0) {
    const tmp = t0;
    t0 = t1;
    t1 = tmp;
  }
  if (t1 - t0 < 0.2) {
    t1 = Math.min(total, t0 + 0.2);
    t0 = Math.max(0, t1 - 0.2);
  }

  // Sample every 0.5 m, and at the source polyline's own vertices.
  const params: number[] = [t0];
  for (const c of cum) if (c > t0 + 0.05 && c < t1 - 0.05) params.push(c);
  for (let t = t0 + SAMPLE; t < t1 - 0.05; t += SAMPLE) params.push(t);
  params.push(t1);
  params.sort((a, b) => a - b);
  const out: Vec2[] = [];
  let last = -Infinity;
  for (const t of params) {
    if (t - last < 0.03 && t < t1 - 1e-9) continue;
    last = t;
    out.push(polyAt(lane, cum, t));
  }
  const pts2 = dedupe(out);
  if (pts2.length < 2) return null;
  return { index, piece, s0, s1, pts: pts2, half: w / 2, hw, ring: null, crossAfter: null };
}

function buildRingLeg(path: DeliveryPath, index: number, piece: RingPiece, s0: number, s1: number): Leg | null {
  // dir -1 = counter-clockwise = right-hand traffic: the OUTER kerb is the
  // travel-right side, so the lane hugs rOut.
  const R = piece.dir === -1 ? piece.rOut - INSET - LANE_HALF : piece.rIn + INSET + LANE_HALF;
  if (R <= 0.2) return null;
  const pA = path.point(s0);
  const pB = path.point(s1);
  const a0 = Math.atan2(pA.x - piece.cx, pA.z - piece.cz);
  let d = wrapAngle(Math.atan2(pB.x - piece.cx, pB.z - piece.cz) - a0);
  if (piece.dir === 1 && d < 1e-3) d += 2 * Math.PI;
  if (piece.dir === -1 && d > -1e-3) d -= 2 * Math.PI;
  const ring: RingLane = { cx: piece.cx, cz: piece.cz, R, a0, a1: a0 + d };
  return { index, piece, s0, s1, pts: ringPts(ring), half: LANE_HALF, hw: 0, ring, crossAfter: null };
}

// --- trimming ------------------------------------------------------------

function lastDir(pts: readonly Vec2[]): Vec2 {
  const n = pts.length;
  return unit(pts[n - 1]!.x - pts[n - 2]!.x, pts[n - 1]!.z - pts[n - 2]!.z);
}

function firstDir(pts: readonly Vec2[]): Vec2 {
  return unit(pts[1]!.x - pts[0]!.x, pts[1]!.z - pts[0]!.z);
}

function lastSegLen(pts: readonly Vec2[]): number {
  const n = pts.length;
  return Math.hypot(pts[n - 1]!.x - pts[n - 2]!.x, pts[n - 1]!.z - pts[n - 2]!.z);
}

function firstSegLen(pts: readonly Vec2[]): number {
  return Math.hypot(pts[1]!.x - pts[0]!.x, pts[1]!.z - pts[0]!.z);
}

/** Tolerance for "same direction" when measuring a leg's straight run. */
const COLLINEAR = Math.cos(0.02);

/**
 * How much STRAIGHT lane a fillet may eat at a leg's end. The leg is sampled
 * every 0.5 m, so its last segment says nothing about how far the tangent may
 * reach: what matters is the run over which the direction does not change.
 */
function straightRunEnd(pts: readonly Vec2[]): number {
  const d = lastDir(pts);
  let len = 0;
  for (let i = pts.length - 1; i > 0; i--) {
    const dx = pts[i]!.x - pts[i - 1]!.x;
    const dz = pts[i]!.z - pts[i - 1]!.z;
    const e = unit(dx, dz);
    if (e.x * d.x + e.z * d.z < COLLINEAR) break;
    len += Math.hypot(dx, dz);
  }
  return len;
}

function straightRunStart(pts: readonly Vec2[]): number {
  const d = firstDir(pts);
  let len = 0;
  for (let i = 0; i + 1 < pts.length; i++) {
    const dx = pts[i + 1]!.x - pts[i]!.x;
    const dz = pts[i + 1]!.z - pts[i]!.z;
    const e = unit(dx, dz);
    if (e.x * d.x + e.z * d.z < COLLINEAR) break;
    len += Math.hypot(dx, dz);
  }
  return len;
}

/** Move a band leg's END to T, which lies on its last segment's line. */
function setEnd(leg: Leg, T: Vec2): void {
  if (leg.ring) {
    leg.ring.a1 = ringAngleNear(leg.ring, leg.ring.a1, T.x, T.z);
    leg.pts = ringPts(leg.ring);
    return;
  }
  const pts = leg.pts;
  const d = lastDir(pts);
  const end = pts[pts.length - 1]!;
  const e = (T.x - end.x) * d.x + (T.z - end.z) * d.z;
  if (e >= 0) {
    pts.push({ x: T.x, z: T.z });
    return;
  }
  let rem = -e;
  while (pts.length > 2 && lastSegLen(pts) <= rem + 1e-9) {
    rem -= lastSegLen(pts);
    pts.pop();
  }
  const n = pts.length;
  const a = pts[n - 2]!;
  const b = pts[n - 1]!;
  const L = Math.hypot(b.x - a.x, b.z - a.z);
  const u = L < 1e-9 ? 0 : Math.max(0, (L - rem) / L);
  pts[n - 1] = { x: a.x + (b.x - a.x) * u, z: a.z + (b.z - a.z) * u };
}

/** Move a band leg's START to T, which lies on its first segment's line. */
function setStart(leg: Leg, T: Vec2): void {
  if (leg.ring) {
    leg.ring.a0 = ringAngleNear(leg.ring, leg.ring.a0, T.x, T.z);
    leg.pts = ringPts(leg.ring);
    return;
  }
  const pts = leg.pts;
  const d = firstDir(pts);
  const start = pts[0]!;
  const e = (T.x - start.x) * d.x + (T.z - start.z) * d.z;
  if (e <= 0) {
    pts.unshift({ x: T.x, z: T.z });
    return;
  }
  let rem = e;
  while (pts.length > 2 && firstSegLen(pts) <= rem + 1e-9) {
    rem -= firstSegLen(pts);
    pts.shift();
  }
  const a = pts[0]!;
  const b = pts[1]!;
  const L = Math.hypot(b.x - a.x, b.z - a.z);
  const u = L < 1e-9 ? 0 : Math.min(1, rem / L);
  pts[0] = { x: a.x + (b.x - a.x) * u, z: a.z + (b.z - a.z) * u };
}

// --- joins ---------------------------------------------------------------

/** Points strictly between P and Q, every ~0.5 m. */
function stitch(P: Vec2, Q: Vec2): Vec2[] {
  const L = Math.hypot(Q.x - P.x, Q.z - P.z);
  const n = Math.floor(L / SAMPLE);
  const out: Vec2[] = [];
  for (let k = 1; k < n; k++) {
    const u = k / n;
    out.push({ x: P.x + (Q.x - P.x) * u, z: P.z + (Q.z - P.z) * u });
  }
  return out;
}

function lineIntersect(pA: Vec2, dA: Vec2, pB: Vec2, dB: Vec2): Vec2 | null {
  const den = dA.x * dB.z - dA.z * dB.x;
  if (Math.abs(den) < 1e-9) return null;
  const a = ((pB.x - pA.x) * dB.z - (pB.z - pA.z) * dB.x) / den;
  return { x: pA.x + dA.x * a, z: pA.z + dA.z * a };
}

/**
 * Fillet of radius R tangent to the band's lane line (p, d) and to the ring's
 * lane circle (O, Rring).
 *
 * Which tangency applies follows from the turn senses, it is not a choice: a
 * fillet that curves the SAME way as the ring nests inside it (|C - O| =
 * |Rring - R|); one that curves the other way — the ordinary entry and exit,
 * where you turn into the ring and the ring then bends back under you — is
 * externally tangent (|C - O| = Rring + R). Getting this wrong reverses the
 * lane at the tangent point. The solution nearest the leg boundary wins.
 */
function bandRingFillet(
  p: Vec2,
  d: Vec2,
  O: Vec2,
  Rring: number,
  ringDir: 1 | -1,
  R: number,
  near: Vec2,
): { C: Vec2; onLine: Vec2; onCircle: Vec2; turn: 1 | -1 } | null {
  const nrm = rightOf(d);
  let best: { C: Vec2; onLine: Vec2; onCircle: Vec2; turn: 1 | -1 } | null = null;
  let bestD = Infinity;
  for (const turn of [1, -1] as const) {
    // dir -1 circulates with the island on the left, i.e. a left turn.
    const want = turn === ringDir ? Math.abs(Rring - R) : Rring + R;
    const q = { x: p.x + nrm.x * R * turn, z: p.z + nrm.z * R * turn };
    const wx = q.x - O.x;
    const wz = q.z - O.z;
    const b = wx * d.x + wz * d.z;
    const disc = b * b - (wx * wx + wz * wz) + want * want;
    if (disc < 0) continue;
    const root = Math.sqrt(disc);
    for (const sgn of [1, -1]) {
      const C = { x: q.x + d.x * (-b + sgn * root), z: q.z + d.z * (-b + sgn * root) };
      const onLine = footOnLine(p, d, C.x, C.z);
      const dl = Math.hypot(C.x - O.x, C.z - O.z);
      if (dl < 1e-6) continue;
      const onCircle = { x: O.x + ((C.x - O.x) / dl) * Rring, z: O.z + ((C.z - O.z) / dl) * Rring };
      const score =
        Math.hypot(onLine.x - near.x, onLine.z - near.z) + Math.hypot(onCircle.x - near.x, onCircle.z - near.z);
      if (score < bestD) {
        bestD = score;
        best = { C, onLine, onCircle, turn };
      }
    }
  }
  return best;
}

/** Arc about C from P to Q, sweeping in the sense `turn` (+1 = right). */
function arcPtsTurn(C: Vec2, P: Vec2, Q: Vec2, turn: 1 | -1): Vec2[] {
  const r0 = Math.hypot(P.x - C.x, P.z - C.z);
  const r1 = Math.hypot(Q.x - C.x, Q.z - C.z);
  const a0 = Math.atan2(P.x - C.x, P.z - C.z);
  let d = wrapAngle(Math.atan2(Q.x - C.x, Q.z - C.z) - a0);
  if (d * turn < 0) d += turn * 2 * Math.PI;
  // Never more than ARC_STEP_RAD of turn between samples: a fillet pulled in
  // tight by a kerb corner would otherwise draw a visible polygon.
  const n = Math.max(3, Math.ceil(Math.abs(d) / ARC_STEP_RAD), Math.ceil((Math.abs(d) * Math.max(r0, r1)) / SAMPLE));
  const out: Vec2[] = [];
  for (let k = 1; k < n; k++) {
    const u = k / n;
    const a = a0 + d * u;
    const r = r0 + (r1 - r0) * u;
    out.push({ x: C.x + Math.sin(a) * r, z: C.z + Math.cos(a) * r });
  }
  return out;
}

/**
 * Does the right-turn fillet of radius R keep the PAINT on the asphalt?
 * The lane's right edge runs at R - half about the fillet centre, and a
 * reflex kerb corner sits only (halfWidth - INSET - lane half) inside the
 * two kerbs, so a wide fillet paints the footway. Same rule as the edge
 * line it continues: paint never crosses a kerb.
 */
function concaveFits(
  network: RoadNetwork,
  X: Vec2,
  dA: Vec2,
  dB: Vec2,
  halfT: number,
  half: number,
  R: number,
): boolean {
  const t = R * halfT;
  const TA = { x: X.x - dA.x * t, z: X.z - dA.z * t };
  const TB = { x: X.x + dB.x * t, z: X.z + dB.z * t };
  const nA = rightOf(dA);
  const C = { x: TA.x + nA.x * R, z: TA.z + nA.z * R };
  const a0 = Math.atan2(TA.x - C.x, TA.z - C.z);
  let d = wrapAngle(Math.atan2(TB.x - C.x, TB.z - C.z) - a0);
  if (d < 0) d += 2 * Math.PI;
  const steps = Math.max(8, Math.ceil((d * R) / 0.02));
  for (let k = 0; k <= steps; k++) {
    const a = a0 + (d * k) / steps;
    const rr = R - half;
    if (network.sdf(C.x + Math.sin(a) * rr, C.z + Math.cos(a) * rr) > 0) return false;
  }
  return true;
}

/** Stitch leg A to leg B; returns the points that go between them. */
function joinLegs(network: RoadNetwork, A: Leg, B: Leg): Vec2[] {
  if (A.pts.length < 2 || B.pts.length < 2) return [];
  const endA = A.pts[A.pts.length - 1]!;
  const startB = B.pts[0]!;
  const dA = lastDir(A.pts);
  const dB = firstDir(B.pts);
  const mid = { x: (endA.x + startB.x) / 2, z: (endA.z + startB.z) / 2 };

  // A ring is an arc, not a line: fillet against its lane circle.
  if (A.ring || B.ring) {
    const ring = (A.ring ?? B.ring)!;
    const band = A.ring ? B : A;
    if (band.ring) return stitch(endA, startB); // ring -> ring: nothing to fillet
    const p = A.ring ? startB : endA;
    const d = A.ring ? dB : dA;
    const ringDir = (A.ring ? A.piece : B.piece) as RingPiece;
    const f = bandRingFillet(p, d, { x: ring.cx, z: ring.cz }, ring.R, ringDir.dir, R_CONCAVE, mid);
    if (!f) return stitch(endA, startB);
    if (A.ring) {
      setEnd(A, f.onCircle);
      setStart(B, f.onLine);
      return arcPtsTurn(f.C, A.pts[A.pts.length - 1]!, B.pts[0]!, f.turn);
    }
    setEnd(A, f.onLine);
    setStart(B, f.onCircle);
    return arcPtsTurn(f.C, A.pts[A.pts.length - 1]!, B.pts[0]!, f.turn);
  }

  // Signed turn: yaw grows to the right in this frame, so left is negative
  // yaw change — the spec's theta is positive to the LEFT.
  const theta = -wrapAngle(Math.atan2(dB.x, dB.z) - Math.atan2(dA.x, dA.z));
  const X = lineIntersect(endA, dA, startB, dB);
  if (X === null || Math.abs(theta) < STRAIGHT_RAD) return stitch(endA, startB);

  const halfT = Math.tan(Math.abs(theta) / 2);
  const paintHalf = Math.max(A.half, B.half);
  let R: number;
  if (theta < 0) {
    // Right turn: the widest fillet up to R_CONCAVE whose painted right edge
    // still clears the reflex kerb corner, never tighter than R_MIN.
    R = R_CONCAVE;
    if (!concaveFits(network, X, dA, dB, halfT, paintHalf, R)) {
      let lo = R_MIN;
      let hi = R_CONCAVE;
      for (let k = 0; k < 14; k++) {
        const mid = (lo + hi) / 2;
        if (concaveFits(network, X, dA, dB, halfT, paintHalf, mid)) lo = mid;
        else hi = mid;
      }
      R = lo;
    }
  } else {
    // Left turn across the junction: the fillet's tangent points must clear
    // both carriageways, so the arc never leaves the asphalt.
    const oA = Math.max(0, A.hw - INSET - A.half);
    const oB = Math.max(0, B.hw - INSET - B.half);
    const sinT = Math.max(0.05, Math.abs(Math.sin(theta)));
    const tNeed = Math.max((B.hw + oB) / sinT, (A.hw + oA) / sinT) + 0.5;
    R = Math.min(R_LEFT_MAX, tNeed / halfT);
  }
  let t = R * halfT;
  const room = Math.min(straightRunEnd(A.pts), straightRunStart(B.pts)) - 0.05;
  if (t > room) {
    R = Math.max(R_MIN, room / halfT);
    t = R * halfT;
  }
  const TA = { x: X.x - dA.x * t, z: X.z - dA.z * t };
  const TB = { x: X.x + dB.x * t, z: X.z + dB.z * t };
  const nA = rightOf(dA);
  const sgn = theta < 0 ? 1 : -1; // centre is to the right for a right turn
  const C = { x: TA.x + nA.x * R * sgn, z: TA.z + nA.z * R * sgn };
  setEnd(A, TA);
  setStart(B, TB);
  return arcPtsTurn(C, A.pts[A.pts.length - 1]!, B.pts[0]!, sgn as 1 | -1);
}

// --- the ribbon ----------------------------------------------------------

/**
 * Build the painted lane's world-space centre line and edges from the road
 * pieces the route visits.
 *
 * `_shape` is unused: the lane is a ROAD MARKING, its geometry a function of
 * the carriageway alone, never of the body driving on it.
 */
export function buildLaneRibbon(
  path: DeliveryPath,
  network: RoadNetwork,
  _shape: RobotShape,
  crossings: ReadonlyArray<{ s0: number; s1: number }> = [],
): GuideEdges {
  // --- a. legs: which piece owns each stretch of the route ----------------
  const raw: Array<{ index: number; s0: number; s1: number }> = [];
  let keep: number | null = null;
  for (let s = 0; s <= path.length + 1e-6; s += SAMPLE) {
    const p = path.point(Math.min(s, path.length));
    const fr = network.frameAt(p.x, p.z, p.theta, keep);
    keep = fr.index;
    const tail = raw[raw.length - 1];
    if (tail && tail.index === fr.index) tail.s1 = Math.min(s, path.length);
    else raw.push({ index: fr.index, s0: Math.min(s, path.length), s1: Math.min(s, path.length) });
  }
  if (raw.length === 0) return extrudeEdges([], [], [], []);
  raw[0]!.s0 = 0;
  raw[raw.length - 1]!.s1 = path.length;
  for (let i = 1; i < raw.length; i++) raw[i]!.s0 = raw[i - 1]!.s1;

  // Merge legs under MIN_LEG_M into their predecessor (the first into its
  // successor, having none).
  for (let pass = 0; pass < 8; pass++) {
    let merged = false;
    for (let i = 0; i < raw.length && raw.length > 1; i++) {
      if (raw[i]!.s1 - raw[i]!.s0 >= MIN_LEG_M) continue;
      if (i > 0) {
        raw[i - 1]!.s1 = raw[i]!.s1;
        raw.splice(i, 1);
      } else {
        raw[1]!.s0 = raw[0]!.s0;
        raw.splice(0, 1);
      }
      merged = true;
      break;
    }
    if (!merged) break;
  }

  // --- e (part 1). Crossings clip the legs around them --------------------
  interface Span {
    index: number;
    s0: number;
    s1: number;
    crossAfter: { s0: number; s1: number } | null;
  }
  let spans: Span[] = raw.map((r) => ({ ...r, crossAfter: null }));
  for (const c of [...crossings].sort((a, b) => a.s0 - b.s0)) {
    const from = c.s0 - TAPER;
    const to = c.s1 + TAPER;
    if (to <= from) continue;
    let iPre = -1;
    for (let i = 0; i < spans.length; i++) if (spans[i]!.s0 < from - 0.25) iPre = i;
    let iPost = -1;
    for (let i = spans.length - 1; i >= 0; i--) if (spans[i]!.s1 > to + 0.25) iPost = i;
    if (iPre < 0 || iPost < 0 || iPost < iPre) continue;
    if (iPre === iPost) {
      const sp = spans[iPre]!;
      const tailSpan: Span = { index: sp.index, s0: to, s1: sp.s1, crossAfter: null };
      sp.s1 = from;
      sp.crossAfter = { s0: c.s0, s1: c.s1 };
      spans.splice(iPre + 1, 0, tailSpan);
    } else {
      spans[iPre]!.s1 = from;
      spans[iPre]!.crossAfter = { s0: c.s0, s1: c.s1 };
      spans[iPost]!.s0 = to;
      spans = [...spans.slice(0, iPre + 1), ...spans.slice(iPost)];
    }
  }

  // --- b/c. leg geometry --------------------------------------------------
  const legs: Leg[] = [];
  for (const sp of spans) {
    const piece = network.pieces[sp.index];
    if (!piece) continue;
    const leg =
      piece.kind === "band"
        ? buildBandLeg(path, sp.index, piece as BandPiece, sp.s0, sp.s1)
        : buildRingLeg(path, sp.index, piece as RingPiece, sp.s0, sp.s1);
    if (!leg) continue;
    leg.crossAfter = sp.crossAfter;
    legs.push(leg);
  }
  if (legs.length === 0) return extrudeEdges([], [], [], []);

  // --- d/e. joins and crossing windows ------------------------------------
  const between: Array<{ pts: Vec2[]; half: number[] }> = [];
  for (let i = 0; i + 1 < legs.length; i++) {
    const A = legs[i]!;
    const B = legs[i + 1]!;
    if (A.crossAfter) {
      const c = A.crossAfter;
      const pA = A.pts[A.pts.length - 1]!;
      const dAe = lastDir(A.pts);
      const pB = B.pts[0]!;
      const dBs = firstDir(B.pts);
      const pts: Vec2[] = [];
      const half: number[] = [];
      for (let s = c.s0 - TAPER + SAMPLE; s < c.s1 + TAPER - 1e-6; s += SAMPLE) {
        const p = path.point(s);
        let u: number;
        let foot: Vec2;
        let legHalf: number;
        if (s <= c.s0) {
          u = Math.min(1, Math.max(0, (s - (c.s0 - TAPER)) / TAPER));
          foot = footOnLine(pA, dAe, p.x, p.z);
          legHalf = A.half;
        } else if (s >= c.s1) {
          u = Math.min(1, Math.max(0, 1 - (s - c.s1) / TAPER));
          foot = footOnLine(pB, dBs, p.x, p.z);
          legHalf = B.half;
        } else {
          u = 1;
          foot = { x: p.x, z: p.z };
          legHalf = LANE_HALF;
        }
        pts.push({ x: foot.x + (p.x - foot.x) * u, z: foot.z + (p.z - foot.z) * u });
        half.push(legHalf + (LANE_HALF - legHalf) * u);
      }
      between.push({ pts, half });
      continue;
    }
    const arc = joinLegs(network, A, B);
    const h0 = A.half;
    const h1 = B.half;
    between.push({
      pts: arc,
      half: arc.map((_, k) => h0 + ((h1 - h0) * (k + 1)) / (arc.length + 1)),
    });
  }

  // --- f. concatenate, dedupe, extrude ------------------------------------
  const cx: number[] = [];
  const cz: number[] = [];
  const half: number[] = [];
  const piece: number[] = [];
  const push = (p: Vec2, h: number, idx: number): void => {
    const n = cx.length;
    if (n > 0 && Math.hypot(p.x - cx[n - 1]!, p.z - cz[n - 1]!) < 0.01) return;
    cx.push(p.x);
    cz.push(p.z);
    half.push(h);
    piece.push(idx);
  };
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i]!;
    for (const p of leg.pts) push(p, leg.half, leg.index);
    const b = between[i];
    if (b) for (let k = 0; k < b.pts.length; k++) push(b.pts[k]!, b.half[k]!, -1);
  }
  return extrudeEdges(cx, cz, half, piece);
}

// --- g. stations ---------------------------------------------------------

function edgeSeries(path: DeliveryPath, E: ReadonlyArray<readonly [number, number]>): Array<{ s: number; d: number }> {
  const out: Array<{ s: number; d: number }> = [];
  let hint: number | null = null;
  for (const p of E) {
    const f = path.project(p[0], p[1], hint);
    hint = f.s;
    out.push({ s: f.s, d: f.d });
  }
  return out;
}

function sampleSeries(series: ReadonlyArray<{ s: number; d: number }>, stations: readonly number[]): number[] {
  const out: number[] = [];
  let i = 0;
  for (const sk of stations) {
    while (i + 2 < series.length && series[i + 1]!.s < sk) i++;
    const a = series[i]!;
    const b = series[i + 1] ?? a;
    if (sk <= a.s) out.push(a.d);
    else if (sk >= b.s) out.push(b.d);
    else out.push(a.d + ((b.d - a.d) * (sk - a.s)) / Math.max(1e-6, b.s - a.s));
  }
  return out;
}

/**
 * Project the finished ribbon back into the planner's frame: one d-interval
 * per station of route progress. The ribbon is the source of truth; these
 * numbers merely describe it.
 */
export function ribbonToStations(path: DeliveryPath, ribbon: GuideEdges): CorridorStation[] {
  const stations: number[] = [];
  for (let s = 0; s <= path.length + 1e-6; s += CORRIDOR_STATION_M) stations.push(Math.min(s, path.length));
  if (ribbon.L.length === 0) return stations.map((s) => ({ s, lo: -LANE_HALF, hi: LANE_HALF }));
  const dl = sampleSeries(edgeSeries(path, ribbon.L), stations);
  const dr = sampleSeries(edgeSeries(path, ribbon.R), stations);
  return stations.map((s, i) => {
    const lo = dl[i]!;
    const hi = Math.max(dr[i]!, lo + 0.3);
    return { s, lo, hi };
  });
}

// --- kerb-side delivery point (ADR-065 amendment K) -----------------------

interface KerbFrame {
  /** Lane centre abeam the query point. */
  point: Vec2;
  /** The lane's own direction there — the road's, in the travel sense. */
  dir: Vec2;
}

/** Foot of (x, z) on a band's own centreline, with that segment's sense. */
function footOnBand(piece: BandPiece, x: number, z: number): { foot: Vec2; dir: Vec2 } {
  let bestD = Infinity;
  let foot: Vec2 = { x, z };
  let dir: Vec2 = { x: 0, z: 1 };
  for (let i = 0; i + 1 < piece.pts.length; i++) {
    const a = piece.pts[i]!;
    const b = piece.pts[i + 1]!;
    const vx = b.x - a.x;
    const vz = b.z - a.z;
    const vv = vx * vx + vz * vz;
    if (vv < 1e-12) continue;
    let u = ((x - a.x) * vx + (z - a.z) * vz) / vv;
    u = u < 0 ? 0 : u > 1 ? 1 : u;
    const fx = a.x + vx * u;
    const fz = a.z + vz * u;
    const d = Math.hypot(x - fx, z - fz);
    if (d < bestD) {
      bestD = d;
      foot = { x: fx, z: fz };
      dir = unit(vx, vz);
    }
  }
  return { foot, dir };
}

/**
 * How far off the travel heading a NAMED road may lie and still be believed:
 * cos 45 deg. Past that the name is describing the road being crossed, not
 * the one being driven.
 */
const NAMED_ALIGN_COS = Math.SQRT1_2;

/** The kerb lane abeam (x, z): its centre point and its direction. */
function kerbLaneFrame(
  network: RoadNetwork,
  x: number,
  z: number,
  headingHint: number,
  pieceId?: string,
): KerbFrame | null {
  // Where two pieces overlap — a traced route and the road it was traced on,
  // a junction stub over its street — `frameAt` picks by heading alone, and
  // two descriptions of the same kerb can differ by 10 deg. Over a wide
  // road's 7 m lane offset that is more than a metre, so when the route
  // NAMES the road it arrives on, that road wins.
  //
  // Only when it is the road actually being DRIVEN, though. A waypoint may
  // name the cross-street it is delivering into, or a junction may be written
  // with the arriving road's name on the far side of it; a piece square to
  // the travel heading has no travel sense at all, so the sign test below
  // degenerates to a tie and the lane centre lands an arbitrary half-road
  // either side. Past 45 deg the name is disbelieved and the point is
  // classified by heading like any other.
  const named = pieceId === undefined ? undefined : network.pieces.find((p) => p.id === pieceId);
  let piece: RoadPiece | undefined;
  // A named ring (or a degenerate band) is no kerb lane either: it is simply
  // disbelieved, and the point is classified by heading like an unnamed one.
  if (named && named.kind === "band" && named.pts.length >= 2) {
    const t = footOnBand(named, x, z).dir;
    const along = Math.abs(t.x * Math.sin(headingHint) + t.z * Math.cos(headingHint));
    if (along >= NAMED_ALIGN_COS) piece = named;
  }
  piece ??= network.frameAt(x, z, headingHint, null).piece;
  if (piece.kind !== "band" || piece.pts.length < 2) return null;
  // A point that is not ON the road has no kerb lane. Without this a route
  // written off the map would be quietly pulled back onto the asphalt, and
  // the validator would never get to say "the route leaves the road".
  if (pieceSdf(piece, x, z) > 0) return null;

  const seat = footOnBand(piece, x, z);
  const foot = seat.foot;
  let dir = seat.dir;
  // Travel direction: the sense of that segment the route is driving in.
  if (dir.x * Math.sin(headingHint) + dir.z * Math.cos(headingHint) < 0) {
    dir = { x: -dir.x, z: -dir.z };
  }

  // The ribbon's own lane width, narrow-road clamp included, so the point is
  // exactly on the paint rather than merely near it.
  const hw = piece.halfWidth;
  let w = LANE_W;
  if (2 * hw < LANE_W + INSET + NARROW_CLEAR) w = Math.max(0.36, 2 * hw - INSET - NARROW_CLEAR);
  const off = hw - INSET - w / 2;
  const nrm = rightOf(dir);
  return { point: { x: foot.x + nrm.x * off, z: foot.z + nrm.z * off }, dir };
}

/**
 * The point on the painted kerb lane abeam (x, z) — where a delivery ends.
 *
 * A route's last waypoint is written on the road's CENTRELINE, so a marker
 * placed there stands in the middle of the carriageway. Real deliveries are
 * made at the kerb, and the blue lane is already where the robot drives, so
 * the end of the route belongs on it.
 *
 * Same rule the ribbon paints with, nothing more: classify the point to a
 * road piece, take the foot of the perpendicular on that piece's centreline,
 * and step to the travel-RIGHT lane centre. A ring (whose lane centre is an
 * arc the route already rides) and a point on no piece are returned unchanged.
 */
export function kerbLanePoint(
  network: RoadNetwork,
  x: number,
  z: number,
  headingHint: number,
  pieceId?: string,
): Vec2 {
  return kerbLaneFrame(network, x, z, headingHint, pieceId)?.point ?? { x, z };
}

/** Metres of easing per metre of kerb offset — a pull-in of about 16 deg. */
const PULL_IN_RATIO = 3.5;
/** Shortest pull-in worth writing into a route. */
const PULL_IN_MIN_M = 4;
/** The pull-in never eats more than this share of the leg it happens on. */
const PULL_IN_SHARE = 0.45;
/** A final leg shorter than this has no room for an ease-in at all. */
const PULL_IN_LEG_MIN_M = 6;
/**
 * Metres along the kerb per metre across it at the steepest arrival allowed
 * when there is no room to ease in — 1.0 would be 45 deg, so this is 42.
 */
const ARRIVE_MIN_RATIO = 1.1;

/**
 * End a route at the kerb (ADR-065 amendment K).
 *
 * A route's last waypoint is written on the road's CENTRELINE, so the delivery
 * — and the marker standing on it — lands in the middle of the carriageway.
 * The delivery belongs on the painted lane, where the robot is driving anyway.
 *
 * Moving the last waypoint sideways on its own would tilt the whole final leg,
 * dragging the route off its written line for tens of metres. So the move is
 * an EASE-IN instead: the route holds its line to a pull-in point `R` metres
 * from the end, then runs diagonally to the kerb. `R` is collinear with the
 * leg it splits, and the kerb point is chosen at the same DISTANCE TO GO as
 * the written end, so the route's length — and with it every anchor, encounter
 * zone and trigger a map keys on distance — is preserved.
 *
 * Preserved EXACTLY on a straight final leg — every map in the pack but the
 * two traced ones. On p01_phenikaa the final leg curves out of a fillet, and
 * the inserted waypoint re-clamps that fillet's radius: the route grows
 * 0.153 m. Nothing keys on distance that finely, but "exactly" would be a lie.
 *
 * Two arrivals cannot be eased in at all, and change the length outright:
 *   - a final leg shorter than `PULL_IN_LEG_MIN_M`, which has no room for the
 *     pull-in: the last waypoint simply moves onto the lane (sliding forward
 *     along it if the leg is shorter than the kerb is far, so the robot still
 *     arrives beside the customer instead of broadside at it);
 *   - a kerb further off than the ease-in reaches, where the route ends abeam
 *     its written end. p02_case1 is the pack's one case: its 16 m-wide traced
 *     ROUTE puts the lane 7.1 m off the centreline, and the route shrinks
 *     0.168 m — 4.255 m the other way mirrored, where that kerb is the far one.
 *
 * Mutates `waypoints` in place and returns the delivery point.
 */
export function snapLastToKerb(waypoints: PathWaypoint[], network: RoadNetwork): Vec2 {
  const n = waypoints.length;
  const last = waypoints[n - 1]!;
  const prev = waypoints[n - 2];
  const land = (p: Vec2): Vec2 => {
    last.x = p.x;
    last.z = p.z;
    return { x: p.x, z: p.z };
  };
  if (!prev) return { x: last.x, z: last.z };
  const legX = last.x - prev.x;
  const legZ = last.z - prev.z;
  const L = Math.hypot(legX, legZ);
  if (L < 1e-6) return { x: last.x, z: last.z };
  const u = { x: legX / L, z: legZ / L };

  const lane = kerbLaneFrame(network, last.x, last.z, Math.atan2(u.x, u.z), last.piece);
  if (!lane) return { x: last.x, z: last.z };
  const mx = lane.point.x - last.x;
  const mz = lane.point.z - last.z;
  const along = mx * u.x + mz * u.z;
  const across = Math.hypot(mx - along * u.x, mz - along * u.z);
  if (across < 1e-9) return land(lane.point);

  /**
   * Land on the lane without a pull-in: at the point abeam the written end,
   * or — when the leg left to turn in is shorter than the kerb is far —
   * far enough further ALONG the lane that the robot still arrives beside the
   * customer rather than broadside across the carriageway. A 1 m leg on a
   * 10 m road otherwise ends on a 76 deg lunge at the kerb.
   */
  const landOnLane = (): Vec2 => {
    const vx = lane.point.x - prev.x;
    const vz = lane.point.z - prev.z;
    const fwd = vx * lane.dir.x + vz * lane.dir.z;
    const side = Math.hypot(vx - fwd * lane.dir.x, vz - fwd * lane.dir.z);
    let slide = Math.max(0, side * ARRIVE_MIN_RATIO - fwd);
    // The slide must stay ON the lane: past the road's end the kerb lane
    // runs out (a 1 m leg on a 16 m road asked for 6 m of slide and landed
    // 1.2 m past the kerb). Keep the largest slide whose landing still sits
    // the lane's full depth inside the road.
    const at = (s: number): Vec2 => ({ x: lane.point.x + lane.dir.x * s, z: lane.point.z + lane.dir.z * s });
    // The abeam point is on the lane centre by construction; its depth is the
    // depth a landing must keep.
    const depth = -network.sdf(lane.point.x, lane.point.z);
    const inside = (s: number): boolean => network.sdf(at(s).x, at(s).z) <= -depth + 0.02;
    if (slide > 0 && !inside(slide)) {
      let lo = 0;
      let hi = slide;
      for (let k = 0; k < 24; k++) {
        const mid = (lo + hi) / 2;
        if (inside(mid)) lo = mid;
        else hi = mid;
      }
      slide = lo;
    }
    return land(at(slide));
  };

  // Too short to ease in on: no pull-in is written, the last waypoint simply
  // moves onto the lane, and the route's length changes with it.
  if (L < PULL_IN_LEG_MIN_M) return landOnLane();

  const R = Math.min(Math.max(PULL_IN_RATIO * across, PULL_IN_MIN_M), PULL_IN_SHARE * L);
  // The delivery slides ALONG THE LANE from the point abeam the written end
  // until it is exactly R from the pull-in point — the same distance to go.
  const ax = mx + u.x * R;
  const az = mz + u.z * R;
  const b = ax * lane.dir.x + az * lane.dir.z;
  const disc = b * b - (ax * ax + az * az) + R * R;
  // No point on the lane is R away from the pull-in point: the kerb is further
  // off than the ease-in reaches. The route then ends abeam its written end,
  // with at least PULL_IN_LEG_MIN_M of leg to cross over in — which is what
  // keeps this from being the lunge the same landing makes on a short leg.
  if (disc < 0) return land(lane.point);
  const root = Math.sqrt(disc);
  const t = Math.abs(-b + root) <= Math.abs(-b - root) ? -b + root : -b - root;

  waypoints.splice(n - 1, 0, {
    x: last.x - u.x * R,
    z: last.z - u.z * R,
    halfWidth: last.halfWidth,
    ...(last.piece !== undefined ? { piece: last.piece } : {}),
  });
  return land({ x: lane.point.x + lane.dir.x * t, z: lane.point.z + lane.dir.z * t });
}
